import { Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import PriceEnrollNodeView from './PriceEnrollNodeView.vue'
import { normalizeHexColor } from './colorAttributes'
import { safeLinkUrl } from './linkAttributes'
import { DEFAULT_BUTTON_COLOR, PRICE_ENROLL_STYLES, priceEnrollButtonStyle, priceEnrollPriceStyle } from './priceEnrollStyles'

const childText = (element, selector) => (element.querySelector(selector)?.textContent || '').trim()

/**
 * "Price and enrolment" block: a big centered price, an enrol button linking to an external
 * form and an optional note. Attributes are read back from the visible HTML (no duplicated
 * JSON), every text goes out as a text node (escaped) and the link/color are validated.
 * Without a valid link no button is emitted; its text is then kept in data-button-text.
 */
export const PriceEnrollNode = Node.create({
  name: 'priceEnroll',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      price: {
        default: '',
        rendered: false,
        parseHTML: element => childText(element, ':scope > h2 strong'),
      },
      buttonText: {
        default: '',
        rendered: false,
        parseHTML: element => childText(element, ':scope > div a') || element.getAttribute('data-button-text') || '',
      },
      buttonUrl: {
        default: '',
        rendered: false,
        parseHTML: element => safeLinkUrl(element.querySelector(':scope > div a')?.getAttribute('href')) || '',
      },
      note: {
        default: '',
        rendered: false,
        parseHTML: element => childText(element, ':scope > p'),
      },
      buttonColor: {
        default: DEFAULT_BUTTON_COLOR,
        rendered: false,
        parseHTML: element => normalizeHexColor(element.getAttribute('data-button-color')) || DEFAULT_BUTTON_COLOR,
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="price-enroll"]' }]
  },

  renderHTML({ node }) {
    const { price, buttonText, note } = node.attrs
    const color = normalizeHexColor(node.attrs.buttonColor) || DEFAULT_BUTTON_COLOR
    const url = safeLinkUrl(node.attrs.buttonUrl)

    const attrs = { 'data-type': 'price-enroll', 'data-button-color': color, style: PRICE_ENROLL_STYLES.wrapper }
    if (!url && buttonText) {
      attrs['data-button-text'] = buttonText
    }

    const children = []
    if (price) {
      children.push(['h2', { style: PRICE_ENROLL_STYLES.heading }, ['strong', { style: priceEnrollPriceStyle(price) }, price]])
    }
    if (url) {
      children.push(['div', { style: PRICE_ENROLL_STYLES.buttonRow }, [
        'a',
        { href: url, target: '_blank', rel: 'noopener noreferrer', style: priceEnrollButtonStyle(color) },
        buttonText,
      ]])
    }
    if (note) {
      children.push(['p', { style: PRICE_ENROLL_STYLES.note }, note])
    }

    return ['div', attrs, ...children]
  },

  addNodeView() {
    return VueNodeViewRenderer(PriceEnrollNodeView)
  },

  addCommands() {
    return {
      insertPriceEnroll: (attrs = {}) => ({ chain }) => {
        return chain()
          .insertContent({
            type: this.name,
            attrs,
          })
          .run()
      },
    }
  },
})

export default PriceEnrollNode
